import { Modal, RadioTile, TileGroup } from '@carbon/react';
import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import sharedMessages from 'Components/utils/sharedMessages';
import { UpdateEvent } from 'api/dtos/event';
import styles from './DeleteEventModal.module.scss';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (mode: UpdateEvent) => void;
    isRecurring?: boolean;
    isLoading?: boolean;
};

const DeleteEventModal = ({
    isOpen,
    onClose,
    onConfirm,
    isRecurring,
    isLoading,
}: Props) => {
    const intl = useIntl();
    const [mode, setMode] = useState<UpdateEvent>(UpdateEvent.Single);

    const handleClose = () => {
        setMode(UpdateEvent.Single);
        onClose();
    };

    const handleConfirm = () => {
        onConfirm(isRecurring ? mode : UpdateEvent.Single);
        setMode(UpdateEvent.Single);
    };

    return (
        <Modal
            open={isOpen}
            onRequestClose={handleClose}
            onRequestSubmit={handleConfirm}
            modalHeading={intl.formatMessage(messages.heading)}
            primaryButtonText={intl.formatMessage(sharedMessages.remove)}
            secondaryButtonText={intl.formatMessage(sharedMessages.cancel)}
            closeButtonLabel={intl.formatMessage(sharedMessages.cancel)}
            primaryButtonDisabled={isLoading}
            danger
            preventCloseOnClickOutside
        >
            {isRecurring && (
                <TileGroup
                    defaultSelected={UpdateEvent.Single}
                    legend={intl.formatMessage(messages.scopeLabel)}
                    valueSelected={mode}
                    onChange={(value) => { setMode(value as UpdateEvent); }}
                    name="delete-event-scope"
                >
                    <RadioTile
                        id="delete-event-single"
                        className={styles.singleElement}
                        value={UpdateEvent.Single}
                    >
                        {intl.formatMessage(messages.singleScope)}
                    </RadioTile>
                    <RadioTile
                        id="delete-event-future"
                        className={styles.singleElement}
                        value={UpdateEvent.Future}
                    >
                        {intl.formatMessage(messages.futureScope)}
                    </RadioTile>
                    <RadioTile
                        id="delete-event-series"
                        value={UpdateEvent.Series}
                    >
                        {intl.formatMessage(messages.seriesScope)}
                    </RadioTile>
                </TileGroup>
            )}
            {!isRecurring && <p>{intl.formatMessage(messages.description)}</p>}
        </Modal>
    );
};

const messages = defineMessages({
    heading: {
        id: 'src.Screens.Dashboard.Events.DeleteEventModal.heading',
        defaultMessage: 'Usuń wydarzenie',
    },
    description: {
        id: 'src.Screens.Dashboard.Events.DeleteEventModal.description',
        defaultMessage: 'Czy na pewno chcesz usunąć to wydarzenie?',
    },
    scopeLabel: {
        id: 'src.Screens.Dashboard.Events.DeleteEventModal.scopeLabel',
        defaultMessage: 'Zakres usuwania',
    },
    singleScope: {
        id: 'src.Screens.Dashboard.Events.DeleteEventModal.singleScope',
        defaultMessage: 'Tylko to wystąpienie',
    },
    futureScope: {
        id: 'src.Screens.Dashboard.Events.DeleteEventModal.futureScope',
        defaultMessage: 'To i wszystkie przyszłe wystąpienia',
    },
    seriesScope: {
        id: 'src.Screens.Dashboard.Events.DeleteEventModal.seriesScope',
        defaultMessage: 'Wszystkie wydarzenia w serii',
    },
});

export default DeleteEventModal;
